import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const StreakTrackerWidget = () => {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedIndex, setAnimatedIndex] = useState(0);
  const navigate = useNavigate();
  const maxStreak = 8;

  useEffect(() => {
    const fetchStreak = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/streak`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        });

        if (!res.ok) throw new Error('Failed to fetch streak');

        const data = await res.json();
        setStreak(data.streak || 0);
      } catch (err) {
        console.error('Error loading streak:', err);
        setStreak(0);
      } finally {
        setLoading(false);
      }
    };

    fetchStreak();
  }, []);

  //manage streak boxes filling in incrementally
  useEffect(() => {
    if (streak === 0) return;
  
    let index = 0;
    let timeout;
  
    const animate = () => {
      index = index + 1;
  
      if (index <= streak) {
        setAnimatedIndex(index);
        timeout = setTimeout(animate, index === streak ? 2400 : 800); // 3x delay on last
      } else {
        setTimeout(() => {
          setAnimatedIndex(0);
          index = 0;
          timeout = setTimeout(animate, 800); // restart
        }, 200);
      }
    };
  
    timeout = setTimeout(animate, 800);
  
    return () => clearTimeout(timeout);
  }, [streak]);  

  const getStreakIcon = (streak) => {
    if (streak >= 8) return '🚀';
    if (streak >= 5) return '🔥🔥';
    if (streak >= 3) return '🔥';
    if (streak >= 1) return '🌱';
    return '🧊';
  };

  const getStreakTitle = (streak) => {
    if (streak >= 8) return '🚀 Elite Streak Achiever!';
    if (streak >= 5) return '🔥 Streak Master!';
    if (streak >= 3) return '🔥 Consistency Champ!';
    if (streak >= 1) return '🌱 Great Start!';
    return '🧊 Get Started!';
  };

  const getStreakMessage = (streak) => {
    if (streak >= 8) return '🚀 Amazing dedication! Keep leading with every conversation!';
    if (streak >= 5) return '🔥 Impressive momentum! Keep pushing your skills forward!';
    if (streak >= 3) return '🔥 Solid progress! Let’s keep the rhythm going!';
    if (streak >= 1) return '🌱 You’ve begun your streak — now build it up!';
    return '🧊 Kick off your first conversation and start your leadership streak!';
  };

  return (
    <div className="relative bg-gray-100 shadow-inner rounded-lg p-6 lg:col-span-2 flex flex-col overflow-hidden">
      {/* Background pattern */}
      <span
        aria-hidden="true"
        style={{
          backgroundImage: "url('/patterns/bubbles.svg')",
          backgroundSize: "200%",
          backgroundPosition: "350px 10px",
          opacity: 0.1,
        }}
        className="pointer-events-none absolute inset-0 bg-repeat z-0"
      />
  
      {/* Main content */}
      <div className="relative z-10">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">Practice Streak</h2>
        {loading ? (
          <p className="text-sm text-gray-500 animate-pulse text-center">Loading streak...</p>
        ) : (
          <>
            {/* Top: Streak summary */}
            <div className="flex flex-col items-center mb-6">
              <p className="text-3xl font-extrabold text-gray-800 mb-5">{streak}-Week Streak</p>
              <div className="flex gap-x-5 transition-transform duration-500 ease-in-out">
                {Array.from({ length: maxStreak }).map((_, i) => {
                  const isFilled = i < animatedIndex;
                  const isLastFilled = i === streak - 1;
                  const isAnimatingLast = isFilled && animatedIndex - 1 === i && i === streak - 1;
  
                  return (
                    <div
                      key={i}
                      className={`
                        h-5 w-5 rounded transition-all duration-300
                        ${isFilled ? 'bg-orange-500' : 'bg-gray-300'}
                        ${isAnimatingLast ? 'scale-150' : isFilled ? 'scale-110' : 'scale-100'}
                      `}
                    />
                  );
                })}
              </div>
            </div>
  
            {/* Bottom stacked content */}
            <div className="flex flex-col items-center gap-4">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="text-6xl mb-2">{getStreakIcon(streak)}</div>
                <div className="text-lg font-semibold text-gray-700">{getStreakTitle(streak)}</div>
              </div>
  
              <div className="bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 p-4 rounded-lg shadow-md flex flex-col items-center justify-center text-center w-full">
                <p className="text-sm text-gray-700 mb-2">{getStreakMessage(streak)}</p>
                <button
                  onClick={() => navigate('/scenarios')}
                  className="mt-2 px-4 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 transition duration-200 w-full sm:w-auto"
                >
                  Explore Scenarios
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );  
};

export default StreakTrackerWidget;
